"use client";

import { c as _c } from "react/compiler-runtime";
import React, { type JSX, useMemo } from "react";
import { useSetWaveData } from "@/contexts/TitleContext";
import { useContentTab } from "../ContentTabContext";
import { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import MyStreamWaveChat from "./MyStreamWaveChat";
import { useWaveData } from "@/hooks/useWaveData";
import MyStreamWaveLeaderboard from "./MyStreamWaveLeaderboard";
import MyStreamWaveOutcome from "./MyStreamWaveOutcome";
import { useSearchParams, usePathname, useRouter } from "next/navigation";
import { WaveWinners } from "@/components/waves/winners/WaveWinners";
import { MyStreamWaveTab } from "@/types/waves.types";
import { MyStreamWaveTabs } from "./tabs/MyStreamWaveTabs";
import MyStreamWaveMyVotes from "./votes/MyStreamWaveMyVotes";
import MyStreamWaveFAQ from "./MyStreamWaveFAQ";
import { useMyStream } from "@/contexts/wave/MyStreamContext";
import { createBreakpoint } from "react-use";
import { getHomeFeedRoute } from "@/helpers/navigation.helpers";
interface MyStreamWaveProps {
  readonly waveId: string;
}
const getContentTabPanelId = (tab: MyStreamWaveTab): string => `my-stream-wave-tabpanel-${tab.toLowerCase()}`;
const useBreakpoint = createBreakpoint({
  LG: 1024,
  S: 0
});
const MyStreamWave: React.FC<MyStreamWaveProps> = t0 => {
  const $ = _c(51);
  const {
    waveId
  } = t0;
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();
  const {
    waves,
    directMessages
  } = useMyStream();
  let t1;
  if ($[0] !== pathname || $[1] !== router || $[2] !== searchParams) {
    t1 = () => {
      const params = new URLSearchParams(searchParams?.toString() || "");
      params.delete("wave");
      const newUrl = params.toString() ? `${pathname}?${params.toString()}` : pathname || getHomeFeedRoute();
      router.push(newUrl, {
        scroll: false
      });
    };
    $[0] = pathname;
    $[1] = router;
    $[2] = searchParams;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t1 || $[5] !== waveId) {
    t2 = {
      waveId,
      onWaveNotFound: t1
    };
    $[4] = t1;
    $[5] = waveId;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const {
    data: wave
  } = useWaveData(t2);
  let t3;
  if ($[7] !== directMessages.list || $[8] !== waveId || $[9] !== waves.list) {
    t3 = waves.list.find(w_0 => w_0.id === waveId) ?? directMessages.list.find(w => w.id === waveId);
    $[7] = directMessages.list;
    $[8] = waveId;
    $[9] = waves.list;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const waveFromList = t3;
  const newDropsCount = waveFromList?.newDropsCount.count ?? 0;
  let t4;
  if ($[11] !== newDropsCount || $[12] !== wave) {
    t4 = wave ? {
      name: wave.name,
      newItemsCount: newDropsCount
    } : null;
    $[11] = newDropsCount;
    $[12] = wave;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  useSetWaveData(t4);
  const stableWaveKey = `wave-${waveId}`;
  const {
    activeContentTab
  } = useContentTab();
  useBreakpoint();
  let t5;
  if ($[14] !== pathname || $[15] !== router || $[16] !== searchParams) {
    t5 = drop => {
      const params_0 = new URLSearchParams(searchParams?.toString() || "");
      params_0.set("drop", drop.id);
      router.push(`${pathname}?${params_0.toString()}`, {
        scroll: false
      });
    };
    $[14] = pathname;
    $[15] = router;
    $[16] = searchParams;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  const onDropClick = t5;
  if (!wave) {
    return null;
  }
  let t6;
  if ($[18] !== wave) {
    t6 = <MyStreamWaveChat wave={wave} />;
    $[18] = wave;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] !== onDropClick || $[21] !== wave) {
    t7 = <MyStreamWaveLeaderboard wave={wave} onDropClick={onDropClick} />;
    $[20] = onDropClick;
    $[21] = wave;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] !== onDropClick || $[24] !== wave) {
    t8 = <WaveWinners wave={wave} onDropClick={onDropClick} />;
    $[23] = onDropClick;
    $[24] = wave;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  let t9;
  if ($[26] !== wave) {
    t9 = <MyStreamWaveOutcome wave={wave} />;
    $[26] = wave;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== onDropClick || $[29] !== wave) {
    t10 = <MyStreamWaveMyVotes wave={wave} onDropClick={onDropClick} />;
    $[28] = onDropClick;
    $[29] = wave;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] !== wave) {
    t11 = <MyStreamWaveFAQ wave={wave} />;
    $[31] = wave;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  let t12;
  if ($[33] !== t10 || $[34] !== t11 || $[35] !== t6 || $[36] !== t7 || $[37] !== t8 || $[38] !== t9) {
    t12 = {
      [MyStreamWaveTab.CHAT]: t6,
      [MyStreamWaveTab.LEADERBOARD]: t7,
      [MyStreamWaveTab.WINNERS]: t8,
      [MyStreamWaveTab.OUTCOME]: t9,
      [MyStreamWaveTab.MY_VOTES]: t10,
      [MyStreamWaveTab.FAQ]: t11
    };
    $[33] = t10;
    $[34] = t11;
    $[35] = t6;
    $[36] = t7;
    $[37] = t8;
    $[38] = t9;
    $[39] = t12;
  } else {
    t12 = $[39];
  }
  const components = t12;
  let t13;
  if ($[40] !== wave) {
    t13 = <MyStreamWaveTabs wave={wave} />;
    $[40] = wave;
    $[41] = t13;
  } else {
    t13 = $[41];
  }
  let t14;
  if ($[42] !== activeContentTab) {
    t14 = getContentTabPanelId(activeContentTab);
    $[42] = activeContentTab;
    $[43] = t14;
  } else {
    t14 = $[43];
  }
  const t15 = components[activeContentTab];
  let t16;
  if ($[44] !== t14 || $[45] !== t15) {
    t16 = <div className="tw-flex-grow tw-overflow-hidden tw-relative" role="tabpanel" id={t14}>{t15}</div>;
    $[44] = t14;
    $[45] = t15;
    $[46] = t16;
  } else {
    t16 = $[46];
  }
  let t17;
  if ($[47] !== stableWaveKey || $[48] !== t13 || $[49] !== t16) {
    t17 = <div className="tailwind-scope tw-relative tw-flex tw-flex-col tw-h-full" key={stableWaveKey}>{t13}{t16}</div>;
    $[47] = stableWaveKey;
    $[48] = t13;
    $[49] = t16;
    $[50] = t17;
  } else {
    t17 = $[50];
  }
  return t17;
};
export default React.memo(MyStreamWave);